import React, { useEffect, useState, useRef } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import GameStatusModal from './gameStatusModal';
import MovesSection from './movesSection';
import { Button, useMediaQuery, useTheme, Paper, Box, Stack, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Undo, Refresh, Share as ShareIcon } from '@mui/icons-material';
import './chessboard.css';
import { useGameStatus } from './gameStatusContext';

type Square = "a8" | "b8" | "c8" | "d8" | "e8" | "f8" | "g8" | "h8" | "a7" | "b7" | "c7" | "d7" | "e7" | "f7" | "g7" | "h7" | "a6" | "b6" | "c6" | "d6" | "e6" | "f6" | "g6" | "h6" | "a5" | "b5" | "c5" | "d5" | "e5" | "f5" | "g5" | "h5" | "a4" | "b4" | "c4" | "d4" | "e4" | "f4" | "g4" | "h4" | "a3" | "b3" | "c3" | "d3" | "e3" | "f3" | "g3" | "h3" | "a2" | "b2" | "c2" | "d2" | "e2" | "f2" | "g2" | "h2" | "a1" | "b1" | "c1" | "d1" | "e1" | "f1" | "g1" | "h1";

interface ChessboardComponentProps {
  fen: string;
  difficulty: 'easy' | 'medium' | 'hard';
  puzzleId: string;
  result: string;
  firstMove: string;
  onPuzzleSolved?: () => void;
  onShare?: () => void;
  onNextDifficulty?: () => void;
}

interface Move {
  moveNumber: number;
  white: string;
  black?: string;
}

const sessionMoveCache: Record<string, string> = {};

const getPositionKey = (fen: string): string => {
  if (!fen) return '';
  return fen.split(' ').slice(0, 4).join(' ');
};

const ChessboardComponent: React.FC<ChessboardComponentProps> = ({ fen, difficulty, puzzleId, result, firstMove, onPuzzleSolved, onShare, onNextDifficulty }) => {
  const { gameStatus, updateGameStatus } = useGameStatus();
  const [chess, setChess] = useState(new Chess(fen));
  const [currentFen, setCurrentFen] = useState(fen);
  const [moveList, setMoveList] = useState<Move[]>([]);
  const [orientation, setOrientation] = useState<'white' | 'black'>('white');
  const [copyButtonText, setCopyButtonText] = useState('Copy');
  const [lastMove, setLastMove] = useState<{ from: Square, to: Square } | null>(null);
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [validMoves, setValidMoves] = useState<Square[]>([]);
  const [promotionMove, setPromotionMove] = useState<{ from: Square, to: Square } | null>(null);
  const [promotionPending, setPromotionPending] = useState(false);
  const userColor = fen ? (fen.split(' ')[1] === 'w' ? 'black' : 'white') : 'white';
  const stockfishWorker = useRef<Worker | null>(null);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isPlayerTurn = chess.turn() === userColor.charAt(0);

  const [premove, _setPremove] = useState<{ from: Square, to: Square } | null>(null);
  const premoveRef = useRef<{ from: Square, to: Square } | null>(null);
  const setPremove = (val: typeof premove) => {
    _setPremove(val);
    premoveRef.current = val;
  };

  const [isThinkingLong, setIsThinkingLong] = useState(false);
  const thinkingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const calculatingFenRef = useRef<string | null>(null);

  const startThinking = () => {
    setIsThinkingLong(false);
    if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
    thinkingTimerRef.current = setTimeout(() => {
      setIsThinkingLong(true);
    }, 1000);
  };

  const stopThinking = () => {
    setIsThinkingLong(false);
    if (thinkingTimerRef.current) {
      clearTimeout(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
    }
  };

  const stopEngine = () => {
    if (stockfishWorker.current) {
      stockfishWorker.current.postMessage("stop");
    }
    calculatingFenRef.current = null;
    stopThinking();
  };

  const executePremove = (chessInstance: Chess) => {
    const p = premoveRef.current;
    if (p) {
      setPremove(null);
      const valid = chessInstance.moves({ square: p.from, verbose: true }).some(m => m.to === p.to);
      if (valid) {
        const piece = chessInstance.get(p.from);
        const isPromo = piece?.type === 'p' && (
          (piece.color === 'w' && p.to[1] === '8') ||
          (piece.color === 'b' && p.to[1] === '1')
        );
        handleMove(p.from, p.to, isPromo ? 'q' : undefined);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
    };
  }, []);

  const [chessboardWidth, setChessboardWidth] = useState(() => {
    if (isMd) return 400;
    if (isSm) return 360;
    if (isXs) return 320;
    return 400;
  });

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const boardWidth = Math.min(screenWidth - 20, 400);
      setChessboardWidth(boardWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const chessInstance = new Chess(fen);
    const userColor = fen?.split(' ')[1] === 'w' ? 'black' : 'white';

    setChess(chessInstance);
    // Don't reset currentFen immediately if it matches? No, always sync to props.
    setCurrentFen(fen);
    updateGameStatus('');
    setOrientation(userColor);
    updateMoveList(chessInstance);

    let timeoutId: NodeJS.Timeout;

    const handleComputerFirstMove = async () => {
      if ((userColor === 'white' && chessInstance.turn() === 'b') ||
        (userColor === 'black' && chessInstance.turn() === 'w')) {
        timeoutId = setTimeout(() => {
          if (chessInstance.fen() !== fen) return;

          const move = chessInstance.move({
            from: firstMove.substring(0, 2),
            to: firstMove.substring(2, 4),
            promotion: firstMove.substring(4, 5) || undefined,
          });
          if (move) {
            setLastMove({ from: move.from, to: move.to });
            setCurrentFen(chessInstance.fen());
            updateMoveList(chessInstance);
            checkGameStatus(chessInstance, result, difficulty, puzzleId);
            executePremove(chessInstance);
          } else {
            console.error('Invalid move:', firstMove);
          }
        }, 1000);
      }
    };

    handleComputerFirstMove();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fen, firstMove, difficulty, puzzleId]);


  const checkGameStatus = (chessInstance: Chess, result: string, difficulty: string, puzzleId: string) => {
    let gameOutcome = '';
    let actualResult = '';
    if (chessInstance.in_checkmate()) {
      const winner = chessInstance.turn() === 'w' ? 'black' : 'white';
      gameOutcome = 'win';
      actualResult = (winner === userColor) ? 'win' : 'lose';
    } else if (chessInstance.in_draw() || chessInstance.in_stalemate() || chessInstance.in_threefold_repetition() || chessInstance.insufficient_material()) {
      gameOutcome = 'draw';
      actualResult = 'draw';
    }

    let newStatus = '';
    if (actualResult === result.toLowerCase()) {
      newStatus = 'YOU WON!';
    } else if (gameOutcome) {
      newStatus = 'GAME OVER';
    }

    if (gameStatus !== newStatus && newStatus !== '') {
      stopEngine();
      updateGameStatus(newStatus);
      updateLocalStorage(difficulty, actualResult === result.toLowerCase() ? 'yes' : 'no', puzzleId);

      if (newStatus === 'YOU WON!' && onPuzzleSolved) {
        onPuzzleSolved();
      }
    }
  };

  const updateLocalStorage = (
    difficulty: string,
    solvedStatus: string,
    puzzleId: string
  ): void => {
    const key = puzzleId;
    const storedData = localStorage.getItem(key);
    let timestamp = Date.now();
    let puzzleData = { easy: {}, medium: {}, hard: {} };

    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.timestamp && parsed.data) {
          timestamp = parsed.timestamp;
          puzzleData = parsed.data;
        } else {
          puzzleData = parsed;
        }
      } catch (e) {
        console.error("Parse error", e);
        // attempt to recover old format if possible? or just reset
      }
    }

    // @ts-ignore
    if (!puzzleData[difficulty]) puzzleData[difficulty] = {};
    // @ts-ignore
    if (!puzzleData[difficulty].solved) {
      // @ts-ignore
      puzzleData[difficulty].solved = solvedStatus;
      // @ts-ignore
    } else if (puzzleData[difficulty].solved !== 'yes') {
      // @ts-ignore
      if (solvedStatus === 'yes' || (puzzleData[difficulty].solved === 'no' && solvedStatus === 'no')) {
        // @ts-ignore
        puzzleData[difficulty].solved = solvedStatus;
      }
    }

    localStorage.setItem(key, JSON.stringify({
      timestamp,
      data: puzzleData
    }));
  };


  const updateMoveList = (chessInstance: Chess) => {
    const history = chessInstance.history({ verbose: true });
    const newMoveList: Move[] = [];
    let moveNumber = 1;
    if (fen?.split(' ')[1] === 'b' && history.length > 0) {
      newMoveList.push({ moveNumber: moveNumber++, white: '...', black: history[0].san });
    }

    for (let i = fen?.split(' ')[1] === 'b' ? 1 : 0; i < history.length; i += 2) {
      const whiteMove = history[i] ? history[i].san : '...';
      const blackMove = history[i + 1] ? history[i + 1].san : '...';
      newMoveList.push({ moveNumber: moveNumber++, white: whiteMove, black: blackMove });
    }

    setMoveList(newMoveList);
  };


  const handleSquareClick = (square: Square) => {
    if (gameStatus) return;

    if (!isPlayerTurn) {
      if (premove) {
        setPremove(null);
        return;
      }
      if (selectedSquare) {
        const piece = chess.get(selectedSquare);
        if (piece && piece.color === userColor.charAt(0)) {
          setPremove({ from: selectedSquare, to: square });
        }
        setSelectedSquare(null);
      } else {
        const piece = chess.get(square);
        if (piece && piece.color === userColor.charAt(0)) {
          setSelectedSquare(square);
        }
      }
      return;
    }

    if (premove) {
      setPremove(null);
    }

    if (selectedSquare) {
      const piece = chess.get(selectedSquare);
      const isPotentialPromotion = piece?.type === 'p' && (
        (piece.color === 'w' && square[1] === '8') ||
        (piece.color === 'b' && square[1] === '1')
      );

      if (isPotentialPromotion) {
        const validMoves = chess.moves({ square: selectedSquare, verbose: true });
        const validPromotionMove = validMoves.some(m => m.to === square && m.promotion);
        if (validPromotionMove) {
          setPromotionMove({ from: selectedSquare, to: square });
          setPromotionPending(true);
          setShowPromotionDialog(true);
          return;
        }
      }

      const moveMade = handleMove(selectedSquare, square);
      if (moveMade) {
        setSelectedSquare(null);
      } else {
        setSelectedSquare(square);
      }
    } else {
      setSelectedSquare(square);
    }
  };

  const handleMove = (sourceSquare: Square, targetSquare: Square, promotion?: string): boolean => {
    try {
      const move: any = { from: sourceSquare, to: targetSquare };
      if (promotion) {
        move.promotion = promotion;
      }

      const moveResult = chess.move(move);
      if (moveResult === null) {
        console.error("Failed move:", { from: sourceSquare, to: targetSquare, promotion });
        return false;
      }
      setLastMove({ from: sourceSquare, to: targetSquare });
      setCurrentFen(chess.fen());
      updateMoveList(chess);
      checkGameStatus(chess, result, difficulty, puzzleId);
      setPromotionMove(null);

      const isGameOver = chess.in_checkmate() || chess.in_draw() || chess.in_stalemate() || chess.in_threefold_repetition() || chess.insufficient_material();
      if (!isGameOver && chess.turn() !== userColor.charAt(0)) {
        makeComputerMove();
      }
      return true;
    } catch (error) {
      console.error("An error occurred during the move attempt:", error instanceof Error ? error.message : "Unknown error");
      setCurrentFen(chess.fen());
      return false;
    }
  };


  const makeComputerMove = () => {
    const fen = chess.fen();
    const posKey = getPositionKey(fen);
    calculatingFenRef.current = fen;

    const isGameOver = chess.in_checkmate() || chess.in_draw() || chess.in_stalemate() || chess.in_threefold_repetition() || chess.insufficient_material();
    if (isGameOver) {
      stopEngine();
      return;
    }

    // Check Cache
    if (sessionMoveCache[posKey]) {
      const cachedMove = sessionMoveCache[posKey];
      console.log("Retrieved move from session cache:", cachedMove);
      startThinking();

      setTimeout(() => {
        if (chess.fen() !== fen) {
          console.warn("Discarding cached move because FEN has changed");
          stopThinking();
          return;
        }
        stopThinking();
        const move = chess.move({
          from: cachedMove.substring(0, 2),
          to: cachedMove.substring(2, 4),
          promotion: cachedMove.substring(4, 5) || undefined,
        });
        if (move) {
          setLastMove({ from: move.from, to: move.to });
          setCurrentFen(chess.fen());
          updateMoveList(chess);
          checkGameStatus(chess, result, difficulty, puzzleId);
          executePremove(chess);
        }
      }, 300);
      return;
    }

    startThinking();
    setTimeout(() => {
      if (chess.fen() !== fen) {
        stopThinking();
        return;
      }
      if (stockfishWorker.current) {
        stockfishWorker.current.postMessage(`position fen ${fen}`);
        stockfishWorker.current.postMessage("go depth 18");
      } else {
        console.error("Stockfish worker is not initialized");
        stopThinking();
      }
    }, 500);
  };

  useEffect(() => {
    if (!stockfishWorker.current) {
      stockfishWorker.current = new Worker(process.env.PUBLIC_URL + '/stockfish-single.js');

      stockfishWorker.current.onerror = (error) => {
        console.error('Stockfish Worker Error:', error);
      };

      stockfishWorker.current.onmessage = (event) => {
        const message = event.data;

        if (message.startsWith('bestmove')) {
          stopThinking();
          const bestMove = message.split(' ')[1];
          
          if (chess.fen() !== calculatingFenRef.current) {
            console.warn("Discarding bestmove because FEN has changed");
            return;
          }

          // Cache the move
          const posKey = getPositionKey(calculatingFenRef.current);
          if (posKey) {
            sessionMoveCache[posKey] = bestMove;
            console.log("Cached bestmove for position:", posKey, "->", bestMove);
          }

          const move = chess.move({
            from: bestMove.substring(0, 2),
            to: bestMove.substring(2, 4),
            promotion: bestMove.substring(4, 5) || undefined,
          });
          if (move) {
            setLastMove({ from: move.from, to: move.to });
            setCurrentFen(chess.fen());
            updateMoveList(chess);
            checkGameStatus(chess, result, difficulty, puzzleId);
            executePremove(chess);
          }
        }
      };
    }

    return () => {
      stopEngine();
      stockfishWorker.current?.terminate();
      stockfishWorker.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chess]);

  const moveBackward = () => {
    stopEngine();
    setPremove(null);
    
    chess.undo();
    setCurrentFen(chess.fen());
    updateMoveList(chess);
    checkGameStatus(chess, result, difficulty, puzzleId);
    setPromotionMove(null);
    setSelectedSquare(null);
    setTimeout(() => {
      chess.undo();
      setCurrentFen(chess.fen());
      updateMoveList(chess);
      checkGameStatus(chess, result, difficulty, puzzleId);
      setSelectedSquare(null);
    }, 400);
  };


  const resetGame = () => {
    stopEngine();
    setPremove(null);

    const chessInstance = new Chess(fen);
    setChess(chessInstance);
    setCurrentFen(fen);
    setMoveList([]);
    updateGameStatus('');
    setLastMove(null);
    setValidMoves([]);
    setSelectedSquare(null);
    setPromotionMove(null);
    setChess(chessInstance);

    if ((userColor === 'white' && chessInstance.turn() === 'b') || (userColor === 'black' && chessInstance.turn() === 'w')) {
      setTimeout(() => {
        if (chessInstance.fen() !== fen) return;

        const move = chessInstance.move({
          from: firstMove.substring(0, 2),
          to: firstMove.substring(2, 4),
          promotion: firstMove.substring(4, 5) || undefined,
        });
        if (move) {
          setLastMove({ from: move.from, to: move.to });
          setCurrentFen(chessInstance.fen());
          updateMoveList(chessInstance);
          checkGameStatus(chess, result, difficulty, puzzleId);
        } else {
          console.error('Invalid move:', firstMove);
        }
      }, 400);
    }
  };

  const copyMovesToClipboard = () => {
    const movesText = moveList.map(movePair => `${movePair.moveNumber}. ${movePair.white} ${movePair.black || ''}`).join(' ');
    navigator.clipboard.writeText(movesText).then(() => {
      setCopyButtonText('Copied...');
      setTimeout(() => {
        setCopyButtonText('Copy');
      }, 500);
    }, (err) => {
      console.error('Could not copy text: ', err);
    });
  };


  const squareStyles = {
    ...(lastMove ? {
      [lastMove.from]: {
        backgroundColor: 'rgba(255, 255, 0, 0.4)'
      },
      [lastMove.to]: {
        backgroundColor: 'rgba(255, 255, 0, 0.4)'
      }
    } : {}),
    ...(selectedSquare ? {
      [selectedSquare]: {
        backgroundColor: 'rgba(255, 255, 0, 0.4)'
      }
    } : {}),
    ...(premove ? {
      [premove.from]: {
        backgroundColor: 'rgba(244, 67, 54, 0.5)'
      },
      [premove.to]: {
        backgroundColor: 'rgba(244, 67, 54, 0.5)'
      }
    } : {}),
    ...validMoves.reduce((styles, square) => {
      styles[square] = {
        backgroundColor: 'grey',
        borderRadius: '90%'
      };
      return styles;
    }, {} as { [key: string]: React.CSSProperties })
  };


  const handlePieceDrop = (sourceSquare: Square, targetSquare: Square): boolean => {
    if (gameStatus) return false;

    if (!isPlayerTurn) {
      const piece = chess.get(sourceSquare);
      if (piece && piece.color === userColor.charAt(0)) {
        setPremove({ from: sourceSquare, to: targetSquare });
      }
      return false;
    }

    const piece = chess.get(sourceSquare);
    const isPromotion = piece?.type === 'p' && (
      (piece.color === 'w' && targetSquare[1] === '8') ||
      (piece.color === 'b' && targetSquare[1] === '1')
    );

    if (isPromotion) {
      setPromotionMove({ from: sourceSquare, to: targetSquare });
      setPromotionPending(true);
      setShowPromotionDialog(false);
      return false;
    }

    const moveMade = handleMove(sourceSquare, targetSquare);
    return moveMade;
  };

  const handlePromotionPieceSelect = (piece: string): boolean => {

    if (!promotionPending) {
      console.error("No promotion is pending, but promotion piece selection was triggered.");
      return false;
    }
    if (!piece || !promotionMove) {
      console.error("Promotion piece or move details are missing.", { piece, promotionMove });
      return false;
    }


    const pieceMap: { [key: string]: string } = {
      'wQ': 'q', 'bQ': 'q',
      'wR': 'r', 'bR': 'r',
      'wB': 'b', 'bB': 'b',
      'wN': 'n', 'bN': 'n',
    };


    const chessJsPromotion = pieceMap[piece] || piece;

    const moveMade = handleMove(promotionMove.from, promotionMove.to, chessJsPromotion);
    if (moveMade) {

      setPromotionMove(null);
      setPromotionPending(false);
      setShowPromotionDialog(false);
    } else {
      console.error('Promotion move failed:', { from: promotionMove.from, to: promotionMove.to, promotion: chessJsPromotion });
      setPromotionMove(null);
      setPromotionPending(false);
      setShowPromotionDialog(false);
    }
    return moveMade;
  };


  return (
    <div>
      <Grid container spacing={0} className="chess-container" sx={{ height: '100%', width: '100%', position: 'relative', zIndex: 1 }}>
        <Grid xs={12} lg={7} container direction="column" spacing={1} alignItems="left">
          <Paper sx={{ position: 'relative', width: chessboardWidth, backgroundColor: 'transparent', boxShadow: 'none', zIndex: 10 }}>
            <Paper elevation={3} sx={{ borderRadius: '4px', overflow: 'visible' }}>
              <Chessboard
                boardWidth={chessboardWidth}
                position={currentFen}
                boardOrientation={orientation}
                onPieceDrop={handlePieceDrop}
                arePiecesDraggable={!gameStatus}
                onSquareClick={handleSquareClick}
                customLightSquareStyle={{ backgroundColor: '#E6E9ED', background: 'linear-gradient(to bottom right, #E6E9ED, #CED2D9)' }}
                customDarkSquareStyle={{ backgroundColor: '#8797A6', background: 'linear-gradient(to bottom right, #91A0B0, #6E7C8C)' }}
                animationDuration={500}
                customSquareStyles={squareStyles}
                showPromotionDialog={showPromotionDialog}
                promotionToSquare={promotionMove ? promotionMove.to : null}
                onPromotionCheck={(sourceSquare: Square, targetSquare: Square, piece: string): boolean => {
                  const pieceObj = chess.get(sourceSquare);
                  const isPotentialPromotion = pieceObj?.type === 'p' && (
                    (pieceObj.color === 'w' && targetSquare[1] === '8') ||
                    (pieceObj.color === 'b' && targetSquare[1] === '1')
                  );
                  if (isPotentialPromotion) {
                    const validMoves = chess.moves({ square: sourceSquare, verbose: true });
                    const validPromotionMove = validMoves.some(m => m.to === targetSquare && m.promotion);
                    if (validPromotionMove) {
                      setPromotionMove({ from: sourceSquare, to: targetSquare });
                      setPromotionPending(true);
                      setShowPromotionDialog(true);
                    } else {
                      return false;
                    }
                  }
                  return isPotentialPromotion;
                }}
                onPromotionPieceSelect={(piece) => {
                  if (!piece) return false;
                  return handlePromotionPieceSelect(piece);
                }}
              />
            </Paper>
            {isThinkingLong && !gameStatus && (
              <Box sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(0, 0, 0, 0.75)',
                color: '#fff',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.8rem',
                zIndex: 15,
                boxShadow: 2
              }}>
                <CircularProgress size={14} color="inherit" />
                <span>Thinking...</span>
              </Box>
            )}
            {gameStatus && (
              <GameStatusModal
                status={gameStatus}
                onReplay={resetGame}
                onClose={() => updateGameStatus('')}
                chessboardWidth={chessboardWidth}
                onNextDifficulty={onNextDifficulty}
              />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, width: chessboardWidth }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Undo />}
                  onClick={moveBackward}
                  sx={{ color: '#375643', borderColor: '#375643', minWidth: 'auto', px: 2 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={resetGame}
                  sx={{ color: '#375643', borderColor: '#375643', minWidth: 'auto', px: 2 }}
                />
              </Stack>
              {onShare && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ShareIcon />}
                  onClick={onShare}
                  sx={{ color: '#375643', borderColor: '#375643', minWidth: 'auto', px: 2 }}
                >
                  Share
                </Button>
              )}
            </Box>

            <Paper sx={{ padding: '8px', mt: 1, textAlign: 'center', width: chessboardWidth, backgroundColor: '#fff', color: '#375643' }}>
              <strong>{userColor.charAt(0).toUpperCase() + userColor.slice(1)} to {result}</strong>
            </Paper>
          </Paper>
        </Grid>
        <Grid xs={12} lg={5} container>
          <MovesSection
            moveList={moveList}
            copyMovesToClipboard={copyMovesToClipboard}
            copyButtonText={copyButtonText}
            chessboardWidth={chessboardWidth}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default ChessboardComponent;
